import { Injectable } from '@angular/core';
import posthog from 'posthog-js';
import { environment } from '../../environments/environment';
import { CookieConsentService } from './cookie-consent.service';
import { APP_VERSION } from '../version';

@Injectable({
  providedIn: 'root',
})
export class PosthogService {
  private isInitialized = false;

  constructor(private consentService: CookieConsentService) {
    // Only auto-initialize if consent is not required OR consent is already granted
    if (!this.consentService.isConsentRequired() || this.consentService.hasAnalyticsConsent()) {
      this.initializePostHog();
    } else {
      console.log('[PostHog] Waiting for user consent before initializing');
    }

    // Listen for consent changes
    this.consentService.consent$.subscribe((consent) => {
      if (consent?.analytics && !this.isInitialized) {
        console.log('[PostHog] Consent granted, initializing PostHog');
        this.initializePostHog();
      } else if (!consent && this.isInitialized) {
        console.log('[PostHog] Consent revoked, opting out of tracking');
        this.optOut();
      }
    });
  }

  private initializePostHog(): void {
    if (this.isInitialized) {
      console.log('[PostHog] Already initialized');
      return;
    }

    if (!environment.posthog || !environment.posthog.apiKey) {
      console.log('[PostHog] Disabled - no API key configured (staging/development environment)');
      return;
    }

    if (environment.posthog && environment.posthog.apiKey) {
      // Install global error handler to suppress iOS Safari iframe errors from PostHog/rrweb
      this.installIframeErrorHandler();

      posthog.init(environment.posthog.apiKey, {
        api_host: environment.posthog.apiHost,
        ui_host: 'https://us.posthog.com',
        loaded: (ph) => {
          if (!environment.production) {
            console.log('PostHog loaded with session recordings enabled', ph);
          }

          // Flush any stored performance data from main.ts bootstrap
          this.flushStoredPerformanceData();
        },
        capture_pageview: false, // We'll handle this manually for better control
        capture_pageleave: true,
        autocapture: true, // Auto-capture clicks, form submissions, etc.
        disable_surveys: true, // Disable surveys to prevent surveys.js loading errors
        // Session recording is enabled by default when this config is present
        session_recording: {
          maskAllInputs: true, // Mask all input fields for privacy
          maskTextSelector: '.sensitive-data, [data-sensitive], .user-email, .payment-info', // Only mask sensitive elements
          maskTextFn: (text) => {
            // Custom masking for emails, phones, and card numbers
            return text
              .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '[EMAIL]')
              .replace(/\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g, '[PHONE]')
              .replace(/\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g, '[CARD]');
          },
          recordCrossOriginIframes: false, // Don't record Stripe iframe
        },
      });

      // Also try to flush immediately (in case loaded callback doesn't fire fast enough)
      setTimeout(() => {
        this.flushStoredPerformanceData();
      }, 100);

      // Register app version as super properties (sent with every event)
      posthog.register({
        app_version: APP_VERSION.version,
        app_version_full: APP_VERSION.fullVersion,
        app_environment: APP_VERSION.environment,
        build_timestamp: APP_VERSION.buildTimestamp,
        build_date: APP_VERSION.buildDate,
        git_commit: APP_VERSION.gitCommit,
      });

      this.isInitialized = true;
      console.log(`[PostHog] Successfully initialized with version ${APP_VERSION.shortVersion}`);
    }
  }

  /**
   * Install global error handler to suppress iOS Safari iframe errors from PostHog/rrweb
   * This prevents the app from crashing when PostHog's session recording tries to access
   * iframe.contentDocument which is blocked by iOS Safari's cross-origin policies
   */
  private installIframeErrorHandler(): void {
    const originalErrorHandler = window.onerror;

    window.onerror = (message, source, lineno, colno, error) => {
      // Check if this is the iOS Safari iframe error from PostHog/rrweb
      const errorMessage = String(message || '');
      const isIframeContentDocumentError =
        errorMessage.includes('contentDocument') ||
        errorMessage.includes('ifameElement') ||
        (errorMessage.includes('null is not an object') &&
         (source?.includes('polyfills') || source?.includes('posthog') || source?.includes('rrweb')));

      // Check if this is a YouTube player error (stopVideo, pauseVideo, etc.)
      const isYouTubePlayerError =
        (errorMessage.includes('stopVideo') ||
         errorMessage.includes('pauseVideo') ||
         errorMessage.includes('playVideo')) &&
        (errorMessage.includes('undefined') || errorMessage.includes('null is not an object'));

      // Check if this is a window.focus() error when window is null (popup blocked)
      const isWindowFocusError =
        (errorMessage.includes('win.focus') ||
         errorMessage.includes('window.focus') ||
         (errorMessage.includes('focus') && errorMessage.includes('null is not an object'))) &&
        (source?.includes('polyfills') || source?.includes('posthog') || source?.includes('vendor') || !source);

      if (isIframeContentDocumentError) {
        // Suppress this error silently - it's a known PostHog/iOS Safari incompatibility
        console.debug('[PostHog] Suppressed iframe access error (iOS Safari compatibility)');
        return true; // Prevent default error handling
      }

      if (isYouTubePlayerError) {
        // Suppress YouTube player errors - player may not be initialized or accessible
        console.debug('[PostHog] Suppressed YouTube player access error (player not initialized)');
        return true; // Prevent default error handling
      }

      if (isWindowFocusError) {
        // Suppress window.focus errors - popup may have been blocked by browser
        console.debug('[PostHog] Suppressed window.focus error (popup blocked or window not available)');
        return true; // Prevent default error handling
      }

      // Pass through all other errors to the original handler
      if (originalErrorHandler) {
        return originalErrorHandler(message, source, lineno, colno, error);
      }

      return false; // Let browser handle the error
    };
  }

  /**
   * Opt out of tracking (when consent is revoked)
   */
  private optOut(): void {
    if (posthog) {
      posthog.opt_out_capturing();
      posthog.reset();
      console.log('[PostHog] User opted out of tracking');
    }
  }

  /**
   * Flush performance data that was stored during app bootstrap
   * This is called after PostHog is initialized to capture early performance metrics
   */
  private flushStoredPerformanceData(): void {
    const perfData = (window as any).__performanceData;
    if (!perfData) {
      return;
    }

    console.log('[PostHog] Flushing stored performance data:', perfData);

    // Capture app launch metrics
    if (perfData.appLaunch) {
      const { data, bootstrapTime, lcp } = perfData.appLaunch;
      
      this.captureEvent('app_launch_time', data);
      console.log('[PostHog] Captured app_launch_time:', Math.round(bootstrapTime), 'ms');

      // Track slow launches separately for high-priority alerts
      if (bootstrapTime > 3000) {
        this.captureEvent('slow_app_launch', {
          bootstrap_time_ms: Math.round(bootstrapTime),
          user_agent: navigator.userAgent,
          connection_type: data.connection_type,
          fcp_ms: data.first_contentful_paint_ms,
          lcp_ms: lcp,
          is_mobile: /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent),
          device_type: /Mobile|Android/i.test(navigator.userAgent) ? 'mobile' : 
                      /iPad|Tablet/i.test(navigator.userAgent) ? 'tablet' : 'desktop'
        });
        console.log('[PostHog] Captured slow_app_launch (slow bootstrap detected)');
      }
    }

    // Capture slow assets
    if (perfData.slowAssets) {
      this.captureEvent('slow_assets_detected', perfData.slowAssets);
      console.log('[PostHog] Captured slow_assets_detected:', perfData.slowAssets.slow_assets.length, 'slow assets');
    }

    // Capture bootstrap errors
    if (perfData.bootstrapError) {
      this.captureEvent('app_bootstrap_error', perfData.bootstrapError);
      console.error('[PostHog] Captured app_bootstrap_error:', perfData.bootstrapError.error_message);
    }

    // Clean up - delete the stored data
    delete (window as any).__performanceData;
    console.log('[PostHog] Performance data flushed and cleaned up');
  }

  /**
   * Capture a custom event
   * @param event Event name
   * @param properties Event properties (optional)
   */
  captureEvent(event: string, properties?: Record<string, any>): void {
    if (this.isInitialized && posthog) {
      posthog.capture(event, properties);
    } else if (!this.isInitialized) {
      console.debug('[PostHog] Event not captured (not initialized):', event);
    }
  }

  /**
   * Capture a page view
   * @param pageName Optional page name (uses current URL if not provided)
   */
  capturePageView(pageName?: string): void {
    if (this.isInitialized && posthog) {
      posthog.capture('$pageview', pageName ? { $current_url: pageName } : {});
    }
  }

  /**
   * Identify a user with a unique ID
   * @param userId Unique user ID
   * @param properties User properties (optional)
   */
  identifyUser(userId: string, properties?: Record<string, any>): void {
    if (this.isInitialized && posthog) {
      posthog.identify(userId, properties);
    }
  }

  /**
   * Set user properties
   * @param properties User properties
   */
  setUserProperties(properties: Record<string, any>): void {
    if (posthog) {
      posthog.people.set(properties);
    }
  }

  /**
   * Alias a user (useful when a user logs in and you want to connect anonymous and identified events)
   * @param alias New identifier for the user
   */
  aliasUser(alias: string): void {
    if (posthog) {
      posthog.alias(alias);
    }
  }

  /**
   * Reset user data (useful on logout)
   */
  reset(): void {
    if (posthog) {
      posthog.reset();
    }
  }

  /**
   * Set a super property (will be sent with all events)
   * @param key Property key
   * @param value Property value
   */
  setSuperProperty(key: string, value: any): void {
    if (posthog) {
      posthog.register({ [key]: value });
    }
  }

  /**
   * Get a super property value
   * @param key Property key
   * @returns Property value or undefined if not set
   */
  getSuperProperty(key: string): any {
    if (posthog) {
      return posthog.get_property(key);
    }
    return undefined;
  }

  /**
   * Set multiple super properties
   * @param properties Properties to register
   */
  setSuperProperties(properties: Record<string, any>): void {
    if (posthog) {
      posthog.register(properties);
    }
  }

  /**
   * Enable or disable session recording
   * @param enabled Whether to enable recording
   */
  toggleSessionRecording(enabled: boolean): void {
    if (posthog) {
      if (enabled) {
        posthog.startSessionRecording();
      } else {
        posthog.stopSessionRecording();
      }
    }
  }

  /**
   * Create a feature flag check
   * @param flag Feature flag key
   * @returns Boolean indicating if flag is enabled
   */
  isFeatureEnabled(flag: string): boolean {
    if (posthog) {
      return posthog.isFeatureEnabled(flag);
    }
    return false;
  }

  /**
   * Get feature flag value (for multivariate flags)
   * @param flag Feature flag key
   * @returns Feature flag value
   */
  getFeatureFlagValue(flag: string): string | boolean | undefined {
    if (posthog) {
      return posthog.getFeatureFlag(flag);
    }
    return undefined;
  }

  /**
   * Get PostHog instance (for advanced use cases)
   */
  getPosthogInstance() {
    return posthog;
  }
}

